/**
 * Tile — service catalogue card (v2, icon-left, Elest.io pattern)
 *
 * ── decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench ────────────
 *
 * THE TILE IS A POINTER. THE APP OPERATIONS BOARD IS THE WORKBENCH.
 *
 * The card carries Manage and Pin. Nothing else. Every per-app verb — edit
 * description, deploy, change requests, user assignment, billing, decommission —
 * lives inside /apps/:id/operations, each control gated by its own role
 * predicate. Moving a control into Manage RELOCATES it; it never re-authorises
 * it, and the server gates are untouched by this file.
 *
 * What this replaced: a single `isAdminCatalog` boolean that answered two
 * independent questions at once — what kind of deployment is this (a property of
 * the INSTANCE) and what may this caller do (a property of the TOKEN). Every
 * defect of 2026-08-01/02 in this area came out of that conflation. They are now
 * two props that cannot be confused:
 *
 *   plane      — shell/routes planeForMode(mode). Owns CARD SHAPE only.
 *                management  = meta broker + vertical broker (+ legacy tier-1-hub):
 *                              lifecycle badge, no commerce CTA, no pricing
 *                              overlay, no muting.
 *                consumption = tenant broker: commerce CTA, View details,
 *                              plans/pricing modal, muting.
 *   canManage  — auth/surfaceAccess canManageApp(caller, tile.tenant). Owns the
 *                Manage affordance only. Computed ONCE, per tile, at the call
 *                site; never re-derived here (the 2026-08-02 rail drift is why).
 *
 * ABSENT, NOT DENIED (decision-2026-08-02-rail-absent-not-denied): a tile this
 * caller cannot manage renders no Manage button — not a disabled one, and not
 * one that opens a refusal.
 *
 * catalogue-card-v2:
 *   - `.dp-card` primitive replaces the old header-strip layout; icon mark
 *     sits left of title/description, footer holds "View details" + deploy meta.
 */

import { useState } from 'react';
import { t } from '../i18n';
import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import type { SurfacePlane } from '../shell/routes';
import TileCTA from './TileCTA';
import SubscribeModal from './SubscribeModal';
import ServiceDetailModal from './ServiceDetailModal';
import PinToggle from './PinToggle';
import { lifecycleBadge } from './catalogBadge';
import type { CartItem } from '../pages/CartPage';

interface TileProps {
  tile: TileConfig;
  /** When true, TileCTA uses POST /bindings flow instead of tile.href */
  useBindingFlow?: boolean;
  onAddToCart?: (item: CartItem) => void;
  /**
   * Which plane this DEPLOYMENT is (ADR-075 vocabulary, shell/routes
   * planeForMode). Card shape only — it says nothing about the caller.
   * Defaults to 'consumption': the tenant broker shape carries no management
   * affordance, so a caller that forgets to pass a plane cannot leak one.
   */
  plane?: SurfacePlane;
  /**
   * May THIS caller manage THIS app — auth/surfaceAccess canManageApp().
   * Defaults false. Only meaningful on the management plane.
   */
  canManage?: boolean;
  /**
   * decision-2026-08-02-ordering-is-a-tenant-admin-verb: may this SURFACE carry a
   * commerce affordance at all? False on every authenticated broker, the tenant
   * broker included — ordering is a tenant-admin verb performed on the vertical
   * broker, not something a tenant user does from their own store.
   *
   * A THIRD question, deliberately kept out of both props above. `plane` owns card
   * SHAPE, so folding the tenant mode into it would flip the lifecycle badge, the
   * detail overlay and the muting with it; `canManage` owns the Manage affordance.
   * Defaults to false so a caller that forgets it renders no commerce, which is the
   * safe direction.
   */
  showCommerce?: boolean;
  /**
   * wp-order-surface-vertical-broker: may THIS caller order THIS app for its
   * tenant — auth/surfaceAccess canOrderForTenant(), computed once per tile at the
   * call site (like canManage, never re-derived here). This is the Order surface of
   * the VERTICAL BROKER: on the management plane a tile whose action is `order`
   * carries an Order button when this is true, opening SubscribeModal. Defaults
   * false, and MarketplacePage only sets it on the vertical broker — so the tenant
   * store (consumption plane) stays CTA-free, absent-not-denied.
   */
  canOrder?: boolean;
  onNavigate?: (path: Route) => void;
  /** brief-26: pin to the top of the catalogue. Management plane only. */
  pinned?: boolean;
  onTogglePin?: () => void;
}

export default function Tile({
  tile, useBindingFlow = false, onAddToCart, plane = 'consumption', canManage = false,
  showCommerce = false, canOrder = false, onNavigate, pinned = false, onTogglePin,
}: TileProps) {
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const management = plane === 'management';
  const isUat = tile.deployment?.phase === 'uat';
  // A private tile carrying the active UAT deployment is actionable for its
  // authorised audience (the server-side ADR-013 predicate already gated it).
  const isMuted = tile.status === 'coming-soon' || (tile.status === 'private' && !isUat);
  /**
   * brief-23: muting is a CONSUMPTION-PLANE signal — opacity 0.66 says "a
   * consumer cannot get this yet". On the management plane the same `private`
   * status is what deriveLifecycle() reports as ONBOARDING, and an onboarding
   * app is exactly the one an operator needs to open: the management
   * affordances were never gated on isMuted, so the card only looked disabled.
   * The badge carries the lifecycle here; the dimming does not belong.
   */
  const dimmed = isMuted && !management;

  /**
   * convention-admin-catalogue-no-commerce-cta (brief-20, tightened in
   * brief-22): a management-plane catalogue is the broker's own service
   * surface, not a tenant store — the tile carries NO cta at all. `order` and
   * `manage` are commerce; `launch` was kept at first but produced an uneven
   * grid (one card with an Open pill made its whole row taller) for an
   * affordance the operator already reaches through the board. Tenant brokers
   * keep the full cta path.
   */
  /**
   * decision-2026-08-02-ordering-is-a-tenant-admin-verb: `order` and `manage` are
   * COMMERCE and now require showCommerce; `launch` is NAVIGATION into an app the
   * tenant already has, and is untouched — it is the whole point of the tenant store.
   * A tile whose action is `order` on a tenant store therefore renders NO cta rather
   * than a disabled one: decision-2026-08-02-rail-absent-not-denied applied to the
   * tile. Offering a button and then refusing the click tells a user a capability
   * exists and they lack the role; on this surface the capability does not exist.
   */
  const isCommerceAction = tile.action === 'order' || tile.action === 'manage';
  /**
   * wp-order-surface-vertical-broker: the ONE commerce affordance the management
   * plane carries. It is not a widening of showCommerce (which owns the storefront
   * shape) but a distinct, per-tile order gate — a tile whose action is `order`,
   * when the caller may order it (canOrderForTenant). This is the vertical broker's
   * Order surface; every other management-plane commerce affordance stays absent.
   */
  const showOrder = management && canOrder && tile.action === 'order';
  const showCta = showOrder || (!management && (!isCommerceAction || showCommerce));
  // With no cta and no View-details link, the footer would render as an empty
  // bordered strip unless the tile has deploy meta to show.
  const showFoot = !management || Boolean(tile.deployment) || showOrder;

  const badgeClass = management
    ? lifecycleBadge(tile).cls
    : isUat
      ? 'dp-badge--uat'
      : tile.status === 'coming-soon'
        ? 'dp-badge--soon'
        : tile.status === 'private'
          ? 'dp-badge--private'
          : 'dp-badge--live';

  const badgeLabel = management
    ? lifecycleBadge(tile).label
    : isUat
      ? t('tile.badge.uat')
      : tile.status === 'coming-soon'
        ? t('tile.badge.soon')
        : tile.status === 'private'
          ? t('tile.badge.private')
          : t('tile.badge.live');

  /**
   * brief-21: the management-plane catalogue has no plans-and-pricing detail
   * modal. An operator's entry point is Manage; a consumer pricing overlay on
   * the broker's own service surface is the tenant store leaking in (same
   * reasoning as the commerce CTA, brief-20).
   */
  function openDetail() {
    if (!isMuted && !management) setShowDetailModal(true);
  }

  // The action row exists only when it would carry something. An empty
  // `.dp-card__adminrow` is a visible gap, and a caller who may neither manage
  // nor pin must see no trace of either.
  const showActionRow = management && (canManage || Boolean(onTogglePin));

  return (
    <>
    <article
      className="dp-card dp-card--badged"
      data-state={dimmed ? 'muted' : undefined}
      onClick={management ? undefined : openDetail}
    >
      <span className={`dp-badge ${badgeClass}`}>{badgeLabel}</span>

      <div className="dp-card__head">
        <div className={`dp-card__mark${dimmed ? ' dp-card__mark--muted' : ''}`} aria-hidden="true">
          {tile.mark}
        </div>
        <div className="dp-card__text">
          <h3 className="dp-card__title">{tile.title}</h3>
          <p className="dp-card__desc">{tile.description}</p>
        </div>
      </div>

      {/* The pointer: Manage + Pin, and nothing else. Deploy and Edit
          description used to live here; they are now sections of the board
          (/apps/:id/operations) with their own role predicates. */}
      {showActionRow && (
        <div className="dp-card__adminrow" onClick={(e) => e.stopPropagation()}>
          {canManage && (
            <button
              type="button"
              className="dp-btn dp-btn--sm dp-btn--ghost"
              onClick={() => onNavigate?.(`/apps/${tile.id}/operations`)}
            >
              {t('tile.manage')}
            </button>
          )}

          {onTogglePin && (
            <span className="dp-card__pinslot">
              <PinToggle pinned={pinned} title={tile.title} onToggle={onTogglePin} />
            </span>
          )}
        </div>
      )}

      {/* Footer: "View details" link + deployment meta (never its own row) + CTA.
          The link is dropped on the management plane — Manage covers what an
          operator needs. Tenant brokers keep it: there it's the consumer's
          primary affordance into plans/pricing. */}
      {showFoot && (
      <div className="dp-card__foot">
        <div className="dp-card__footleft">
          {!management && (
            <button
              type="button"
              className="dp-link"
              disabled={isMuted}
              aria-label={t('serviceDetail.openAriaLabel').replace('{{service}}', tile.title)}
              onClick={(e) => { e.stopPropagation(); openDetail(); }}
            >
              {t('tile.viewDetails')}
            </button>
          )}
          {tile.deployment && (
            <>
              {!management && (
                <span className="dp-card__sep" aria-hidden="true">·</span>
              )}
              <p className="dp-card__meta">
                {t('tile.deployment')
                  .replace('{{no}}', String(tile.deployment.deployNo))
                  .replace('{{date}}', tile.deployment.deployDate)}
              </p>
            </>
          )}
        </div>

        {/* stopPropagation so body-click doesn't double-trigger */}
        {showCta && (
          <div className="dp-card__cta" onClick={(e) => e.stopPropagation()}>
            <TileCTA
              tile={tile}
              useBindingFlow={useBindingFlow}
              onOrder={showCommerce || showOrder ? () => setShowSubscribeModal(true) : undefined}
            />
          </div>
        )}
      </div>
      )}
    </article>

    {showDetailModal && (
      <ServiceDetailModal
        tile={tile}
        plane={plane}
        showCommerce={showCommerce}
        onClose={() => setShowDetailModal(false)}
        onOrder={() => {
          setShowDetailModal(false);
          setShowSubscribeModal(true);
        }}
        onAddToCart={onAddToCart}
      />
    )}

    {/* Mounted only on a surface that carries commerce — the marketing storefront
        (showCommerce) or the vertical broker's Order affordance (showOrder).
        SubscribeModal POSTs a subscription, and the server rejects that from a
        non-order role anyway (infraops ORDER_ROLES). Belt and braces. */}
    {showSubscribeModal && (showCommerce || showOrder) && (
      <SubscribeModal tile={tile} onClose={() => setShowSubscribeModal(false)} />
    )}
    </>
  );
}
